/* Help & guides drawer (prototype helpDrawer + gettingStarted): search across all guides,
 * "On this page" first, steps, tips, troubleshooting, getting-started checklist, tour. */
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n";
import { useOverlays } from "../../app/overlays";
import { useSession } from "../../app/session";
import { useAllEmployees, useBilling, usePayrollHistory, useSettings } from "../../data/hooks";
import { AiNote, Bar, Button, Icon, Modal, Steps, cx, useToast } from "../../ui";
import { GUIDES, guideKeyFor, searchGuides } from "./guides";
import "./help.css";

export const SETUP_STORE = "rukn-setup";
function loadSetup(): Record<string, number> {
  try { const v = JSON.parse(localStorage.getItem(SETUP_STORE) || "{}"); return v && typeof v === "object" ? v : {}; } catch { return {}; }
}
function saveSetup(v: Record<string, number>) {
  try { localStorage.setItem(SETUP_STORE, JSON.stringify(v)); } catch { /* storage blocked */ }
}

export function GettingStarted({ onGo }: { onGo: () => void }) {
  const { L } = useI18n();
  const { role } = useSession();
  const ov = useOverlays();
  const nav = useNavigate();
  const oh = role === "owner" || role === "hr";
  const settings = useSettings({ enabled: oh });
  const emps = useAllEmployees({ enabled: oh });
  const hist = usePayrollHistory({ enabled: oh });
  const billing = useBilling({ enabled: role === "owner" });
  const [marks, setMarks] = useState(loadSetup);
  if (!oh) return null;
  const items: [string, string, string, boolean][] = [
    ["company", L("Add company details", "أضف بيانات المنشأة"), "/settings", !!settings.data?.tenant.cr],
    ["employees", L("Import your employees", "استورد الموظفين"), "/employees", (emps.data?.length ?? 0) > 0],
    ["gosi", L("Check GOSI & allowance settings", "راجع إعدادات التأمينات والبدلات"), "/settings", false],
    ["devices", L("Connect a biometric device", "اربط جهاز بصمة"), "/biometric", false],
    ["payroll", L("Run your first payroll", "شغّل أول مسير رواتب"), "/payroll/run", !!hist.data?.some((h) => h.status === "Paid")],
    ["invite", L("Invite employees to the app", "ادعُ الموظفين للتطبيق"), "/employees", false],
  ];
  if (role === "owner") items.push(["subscribe", L("Choose a plan before the trial ends", "اختر باقة قبل انتهاء التجربة"), "/billing", billing.data?.subscription.status === "active"]);
  const isDone = (x: (typeof items)[number]) => x[3] || !!marks[x[0]];
  const done = items.filter(isDone).length;
  const go = (x: (typeof items)[number]) => { const v = { ...marks, [x[0]]: 1 }; setMarks(v); saveSetup(v); onGo(); nav(x[2]); };
  return (
    <section className="card gs" data-testid="getting-started">
      <div className="card-h">
        <div><h3>{L("Getting started", "البدء")}</h3><p>{L(done + " of " + items.length + " done", "أُنجز " + done + " من " + items.length)}</p></div>
        <Button variant="sm" icon="sparkle" onClick={() => ov.open({ kind: "tour" })}>{L("Take the tour", "ابدأ الجولة")}</Button>
      </div>
      <div className="card-b">
        <div className="mb"><Bar value={done} max={items.length} /></div>
        <div className="gs-list" style={{ gridTemplateColumns: "minmax(0, 1fr)" }}>
          {items.map((x) => {
            const d = isDone(x);
            return (
              <button type="button" key={x[0]} className={cx("gs-item", d && "done")} onClick={() => go(x)} data-testid={"gs-" + x[0]} style={{ background: "transparent", textAlign: "start", cursor: "pointer" }}>
                <span className="gs-c">{d && <Icon name="check" className="s" />}</span><span>{x[1]}</span>{!d && <Icon name="chevron-right" className="s" />}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function HelpDrawer({ topic, onClose }: { topic?: string; onClose: () => void }) {
  const { L } = useI18n();
  const ov = useOverlays();
  const loc = useLocation();
  const toast = useToast();
  const { role } = useSession();
  const pageKey = guideKeyFor(loc.pathname);
  const [cur, setCur] = useState<string | null>(topic && GUIDES[topic] ? topic : pageKey);
  const [q, setQ] = useState("");
  const keys = useMemo(() => searchGuides(q), [q]);
  const g = cur ? GUIDES[cur] : null;
  const pick = (k: string) => { setCur(k); setQ(""); document.querySelector(".modal.drawer .mb2")?.scrollTo?.({ top: 0 }); };

  return (
    <Modal title={L("Help & guides", "المساعدة والأدلة")} className="drawer" onClose={onClose} testId="help-drawer">
      <div className="stack">
        <div className="search"><Icon name="search" /><input className="inp" value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Search help… e.g. WPS, Iqama, device", "ابحث… مثل حماية الأجور، الإقامة، الجهاز")} aria-label={L("Search help", "البحث في المساعدة")} data-testid="help-search" /></div>

        {g && !q && cur && (
          <section className="card card-b" data-testid="help-current" data-guide={cur}>
            <div className="prs-h">{cur === pageKey ? L("On this page", "في هذه الصفحة") : L("Guide", "الدليل")}</div>
            <h3 style={{ fontSize: 16, marginBottom: 10 }}>{L(g.t[0], g.t[1])}</h3>
            <Steps steps={g.steps.map((s) => L(s[0], s[1]))} />
            {g.tips && <div className="mt"><AiNote>{g.tips.map((x) => L(x[0], x[1])).join(" ")}</AiNote></div>}
            {g.trouble && (
              <div className="help-trouble" data-testid="help-trouble">
                <div className="prs-h" style={{ margin: "4px 0 0" }}>{L("Troubleshooting", "حل المشكلات")}</div>
                {g.trouble.map(([qq, a], i) => <details key={i}><summary>{L(qq[0], qq[1])}</summary><p>{L(a[0], a[1])}</p></details>)}
              </div>
            )}
            {g.see && (
              <div className="help-see">
                {g.see.filter((k) => GUIDES[k]).map((k) => <Button key={k} variant="ghost sm" icon="book" onClick={() => pick(k)} data-testid={"help-see-" + k}>{L(GUIDES[k].t[0], GUIDES[k].t[1])}</Button>)}
              </div>
            )}
            {cur !== pageKey && pageKey && <div className="mt"><Button variant="sm" onClick={() => pick(pageKey)} data-testid="help-back">{L("Back to this page's guide", "العودة لدليل هذه الصفحة")}</Button></div>}
          </section>
        )}

        {!q && <GettingStarted onGo={onClose} />}

        <div className="prs-h" style={{ margin: "4px 0 0" }}>{q ? L("Results", "النتائج") + " · " + keys.length : L("All guides", "كل الأدلة")}</div>
        <div className="help-list" data-testid="help-results">
          {keys.length === 0 && <p className="muted sm" data-testid="help-empty">{L("No guides match. Try another word or ask the AI assistant.", "لا توجد نتائج. جرّب كلمة أخرى أو اسأل المساعد الذكي.")}</p>}
          {keys.map((k) => (
            <button type="button" key={k} className={cx("cmd-i", k === cur && "on")} onClick={() => pick(k)} data-testid={"help-item-" + k}>
              <span className="kpi-ico"><Icon name="file" className="s" /></span><span>{L(GUIDES[k].t[0], GUIDES[k].t[1])}</span>
            </button>
          ))}
        </div>

        <div className="divider" />
        <div className="row">
          <Button variant="sm" icon="sparkle" onClick={() => ov.open({ kind: "tour" })} data-testid="help-tour">{L("Take the tour", "ابدأ الجولة")}</Button>
          {role !== "admin" && <Button variant="sm" icon="sparkle" onClick={() => ov.open({ kind: "assistant" })} data-testid="help-assistant">{L("Ask the AI assistant", "اسأل المساعد")}</Button>}
          <Button variant="sm ghost" icon="mail" onClick={() => toast(L("Support: support@ruknhr.sa · +966 11 000 0000 (Sun–Thu 9–6)", "الدعم: support@ruknhr.sa · ‎+966 11 000 0000 (الأحد–الخميس 9–6)"), "info")} data-testid="help-support">{L("Contact support", "تواصل مع الدعم")}</Button>
        </div>
      </div>
    </Modal>
  );
}
